import clsx from 'clsx'
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react'

interface App {
  name: string
  score: number
  status: 'compliant' | 'violations' | 'critical'
}

interface AppStatusGridProps {
  apps: App[]
}

export default function AppStatusGrid({ apps }: AppStatusGridProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'violations':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return null
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600'
    if (score >= 0.7) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800'
      case 'violations':
        return 'bg-yellow-100 text-yellow-800'
      case 'critical':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Application Status
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {apps.map((app) => (
          <div
            key={app.name}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                {getStatusIcon(app.status)}
                <h4 className="ml-2 font-medium text-gray-900 capitalize">
                  {app.name}
                </h4>
              </div>
              <span
                className={clsx(
                  'text-2xl font-bold',
                  getScoreColor(app.score)
                )}
              >
                {Math.round(app.score * 100)}%
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span
                className={clsx(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  getStatusBadge(app.status)
                )}
              >
                {app.status}
              </span>
              <span className="text-xs text-gray-500">
                Last scan: 2h ago
              </span>
            </div>

            {/* Mini progress bar */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={clsx(
                    'h-2 rounded-full transition-all duration-500',
                    app.score >= 0.9 ? 'bg-green-500' :
                    app.score >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'
                  )}
                  style={{ width: `${app.score * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Average Compliance</span>
          <span className="font-medium text-gray-900">
            {apps.length > 0
              ? `${Math.round(
                  (apps.reduce((sum, app) => sum + app.score, 0) / apps.length) * 100
                )}%`
              : '—'}
          </span>
        </div>
      </div>
    </div>
  )
}